/*
  Mail Settings Component
  -----------------------
  Supports Mailgun and MS Graph (Microsoft 365) for email sending.
  SMTP is disabled (Netlify blocks outbound SMTP).
  
  Required Environment Variables:
  
  For Mailgun:
    - MAILGUN_API_KEY: Mailgun API key
    - MAILGUN_DOMAIN: Mailgun domain
    - MAILGUN_FROM: From address
    - MAILGUN_REGION: US or EU (default: US)
  
  For MS Graph (Microsoft 365):
    - MS_TENANT_ID: Azure AD tenant ID
    - MS_CLIENT_ID: App registration client ID
    - MS_CLIENT_SECRET: App registration client secret
    - MS_FROM_EMAIL: Sender email address (must have Mail.Send permission)
*/

import React, { useState, useEffect, useCallback } from 'react';

const MailSettings = ({ showDebug = false }) => {
  const [mailMethod, setMailMethod] = useState('msgraph');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [envVars, setEnvVars] = useState({ mailgun: {}, msgraph: {} });
  
  // Test email state
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testEmailBusy, setTestEmailBusy] = useState(false);
  const [testEmailMsg, setTestEmailMsg] = useState('');

  // Load settings from server
  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('roi_admin_token');
      const res = await fetch('/.netlify/functions/admin-get-settings', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json().catch(() => ({}));
      if (data.method) {
        setMailMethod(data.method);
      }
      if (data.envVars) {
        setEnvVars(data.envVars);
      }
    } catch (e) {
      console.error('Failed to load mail settings:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Save mail method selection
  const saveMethod = async (method) => {
    setSaving(true);
    setMessage('');
    try {
      const token = localStorage.getItem('roi_admin_token');
      const res = await fetch('/.netlify/functions/admin-save-mail-method', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ method })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(data?.error || 'Failed to save');
        return;
      }
      setMailMethod(method);
      setMessage(`Mail method set to ${method === 'msgraph' ? 'MS Graph' : 'Mailgun'}`);
    } catch (e) {
      setMessage(String(e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  // Send test email
  const sendTestEmail = async () => {
    if (!testEmailTo) {
      setTestEmailMsg('Please enter an email address.');
      return;
    }
    setTestEmailBusy(true);
    setTestEmailMsg('');
    try {
      const token = localStorage.getItem('roi_admin_token');
      const res = await fetch('/.netlify/functions/send-mail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          to: testEmailTo,
          subject: 'Test Email from ROI Calculator Admin',
          text: 'This is a test email sent from the Admin Dashboard.',
          html: '<p>This is a <strong>test email</strong> sent from the Admin Dashboard.</p>'
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Build detailed error message
        let errMsg = data?.error || 'Failed to send test email';
        if (data?.details) errMsg += `: ${data.details}`;
        if (data?.status) errMsg += ` (HTTP ${data.status})`;
        if (data?.msGraphError) errMsg += `\n\nMS Graph Error: ${data.msGraphError}`;
        if (data?.mailgunError) errMsg += `\n\nMailgun Error: ${data.mailgunError}`;
        if (data?.debug) errMsg += `\n\nDebug: ${JSON.stringify(data.debug, null, 2)}`;
        throw new Error(errMsg);
      }
      
      // Success - check for fallback
      if (data.fallback) {
        setTestEmailMsg(`⚠️ ${data.fallback}\n\nMS Graph Error: ${data.msGraphError}`);
      } else {
        setTestEmailMsg(data.message || 'Test email sent successfully!');
      }
    } catch (e) {
      setTestEmailMsg(String(e?.message || e));
    } finally {
      setTestEmailBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Mail Delivery Settings</h2>
      
      {/* Method Selection */}
      <div className="p-4 bg-gray-50 rounded-lg border">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Select Mail Provider
        </label>
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Mailgun Option */}
          <label 
            className={`flex-1 p-4 border rounded-lg cursor-pointer transition-all ${
              mailMethod === 'mailgun' 
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="mailmethod"
                value="mailgun"
                checked={mailMethod === 'mailgun'}
                onChange={() => saveMethod('mailgun')}
                disabled={saving}
                className="mt-1"
              />
              <div>
                <div className="font-medium">Mailgun</div>
                <div className="text-sm text-gray-500">
                  Mailgun email service
                </div>
              </div>
            </div>
          </label>
          
          {/* MS Graph Option */}
          <label 
            className={`flex-1 p-4 border rounded-lg cursor-pointer transition-all ${
              mailMethod === 'msgraph' 
                ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start gap-3">
              <input
                type="radio"
                name="mailmethod"
                value="msgraph"
                checked={mailMethod === 'msgraph'}
                onChange={() => saveMethod('msgraph')}
                disabled={saving}
                className="mt-1"
              />
              <div>
                <div className="font-medium">MS Graph (Microsoft 365)</div>
                <div className="text-sm text-gray-500">
                  Send from your Office 365 mailbox
                </div>
              </div>
            </div>
          </label>
        </div>
        
        {message && (
          <p className={`mt-3 text-sm ${message.includes('Failed') ? 'text-red-600' : 'text-green-600'}`}>
            {message}
          </p>
        )}
        
        {saving && (
          <p className="mt-3 text-sm text-gray-500">Saving...</p>
        )}
      </div>

      {/* Configuration - Current Values */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-medium text-blue-800 mb-2">
          Current {mailMethod === 'msgraph' ? 'MS Graph' : 'Mailgun'} Configuration
        </h3>
        <p className="text-sm text-blue-700 mb-3">
          Values from Netlify environment variables (secrets masked):
        </p>
        <div className="font-mono text-sm space-y-1">
          {mailMethod === 'msgraph' ? (
            <>
              <div className="flex"><span className="text-blue-600 w-40">MS_TENANT_ID:</span> <span className="text-gray-700">{loading ? '(loading...)' : (envVars.msgraph?.MS_TENANT_ID || '(not set)')}</span></div>
              <div className="flex"><span className="text-blue-600 w-40">MS_CLIENT_ID:</span> <span className="text-gray-700">{loading ? '(loading...)' : (envVars.msgraph?.MS_CLIENT_ID || '(not set)')}</span></div>
              <div className="flex"><span className="text-blue-600 w-40">MS_CLIENT_SECRET:</span> <span className="text-gray-700">{loading ? '(loading...)' : (envVars.msgraph?.MS_CLIENT_SECRET || '(not set)')}</span></div>
              <div className="flex"><span className="text-blue-600 w-40">MS_FROM_EMAIL:</span> <span className="text-gray-700">{loading ? '(loading...)' : (envVars.msgraph?.MS_FROM_EMAIL || '(not set)')}</span></div>
            </>
          ) : (
            <>
              <div className="flex"><span className="text-blue-600 w-40">MAILGUN_API_KEY:</span> <span className="text-gray-700">{loading ? '(loading...)' : (envVars.mailgun?.MAILGUN_API_KEY || '(not set)')}</span></div>
              <div className="flex"><span className="text-blue-600 w-40">MAILGUN_DOMAIN:</span> <span className="text-gray-700">{loading ? '(loading...)' : (envVars.mailgun?.MAILGUN_DOMAIN || '(not set)')}</span></div>
              <div className="flex"><span className="text-blue-600 w-40">MAILGUN_FROM:</span> <span className="text-gray-700">{loading ? '(loading...)' : (envVars.mailgun?.MAILGUN_FROM || '(not set)')}</span></div>
              <div className="flex"><span className="text-blue-600 w-40">MAILGUN_REGION:</span> <span className="text-gray-700">{loading ? '(loading...)' : (envVars.mailgun?.MAILGUN_REGION || '(not set)')}</span></div>
            </>
          )}
        </div>
      </div>

      {/* Configuration Help */}
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="font-medium text-yellow-800 mb-2">How to Configure</h3>
        <p className="text-sm text-yellow-700 mb-2">
          Go to your Netlify dashboard → Site settings → Environment variables.
        </p>
        {mailMethod === 'msgraph' ? (
          <div className="text-sm text-yellow-700">
            <p className="font-medium mb-1">MS Graph Setup:</p>
            <ol className="list-decimal list-inside space-y-1 text-xs">
              <li>Register an app in Entra ID (Azure AD)</li>
              <li>Add API permission: Microsoft Graph → Application → Mail.Send</li>
              <li>Grant admin consent for the permission</li>
              <li>Create a client secret</li>
              <li>Set the env vars with your tenant ID, client ID, secret, and sender email</li>
            </ol>
          </div>
        ) : (
          <p className="text-sm text-yellow-700">
            Get your Mailgun API key and domain from your Mailgun dashboard.
          </p>
        )}
      </div>

      {/* Test Email */}
      <div className="p-4 border rounded-lg">
        <h3 className="font-medium mb-3">Send Test Email</h3>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={testEmailTo}
            onChange={e => setTestEmailTo(e.target.value)}
            className="flex-1 border rounded px-3 py-2"
            placeholder="you@example.com"
          />
          <button
            onClick={sendTestEmail}
            disabled={testEmailBusy}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
          >
            {testEmailBusy ? 'Sending...' : 'Send Test'}
          </button>
        </div>
        {testEmailMsg && (
          <pre className={`mt-2 text-sm whitespace-pre-wrap ${testEmailMsg.includes('success') ? 'text-green-600' : 'text-red-600'}`}>
            {testEmailMsg}
          </pre>
        )}
      </div>

      {showDebug && (
        <div className="p-4 bg-gray-100 rounded text-xs">
          <div>Current method: {mailMethod}</div>
        </div>
      )}
    </div>
  );
};

export default MailSettings;
